import { TOP_LAYER_VARS } from '@src/components/common/topLayerZ';
import { CLIP_PREVIEW_Z } from '@src/components/content-components/clipPreviewZ';
import SimplePencilIcon from '@src/svgs/SimplePencilIcon';
import React, { useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { COVERAGE_TOOLTIP_STYLE } from './CoverageBadge';

export const PHRASE_EDIT_PENCIL_TOOLTIP_ID = 'phrase-edit-pencil-tooltip';

/** The pencil's square hit area, in px. */
export const PENCIL_SIZE = 20;
const GAP = 4;
const VIEWPORT_MARGIN = 4;

export interface PhraseEditPencilProps {
  /** The hovered translated phrase; the pencil sits at the end of its last line. */
  anchorElement: HTMLElement;
  /** Open the in-place field for that phrase. */
  onEdit: () => void;
  /** The pointer moved onto / off the pencil — the host's hover bridge. */
  onHoverChange: (inside: boolean) => void;
}

/**
 * PLAT-11650 — the always-there way into the in-place field, and the thing that
 * teaches the gesture: hovering a translated phrase in Paragraph / Segment
 * shows one pencil at the end of the phrase; clicking it opens the field, its
 * tooltip says the double-click also does.
 *
 * ONE pencil for the whole column, portaled to the body, placed beside the
 * hovered phrase — never a child of the phrase span. The translated column is
 * a flat run of sibling spans that text selection depends on (CLAUDE.md
 * pitfall 11): a control inside each span would shift the words on hover and
 * sit inside the selectable flow. Placed at the phrase's LAST client rect
 * because a wrapped phrase ends mid-line, not at its bounding box's corner.
 */
export const PhraseEditPencil: React.FC<PhraseEditPencilProps> = ({
  anchorElement,
  onEdit,
  onHoverChange,
}) => {
  const [placement, setPlacement] = useState(() =>
    computePencilPlacement(anchorElement),
  );
  const anchorRef = useRef(anchorElement);
  anchorRef.current = anchorElement;

  useLayoutEffect(() => {
    setPlacement(computePencilPlacement(anchorElement));
    // The column scrolls under a fixed pencil: follow it.
    const follow = () =>
      setPlacement(computePencilPlacement(anchorRef.current));
    window.addEventListener('scroll', follow, true);
    window.addEventListener('resize', follow);
    return () => {
      window.removeEventListener('scroll', follow, true);
      window.removeEventListener('resize', follow);
    };
  }, [anchorElement]);

  return ReactDOM.createPortal(
    <>
      <Pencil
        type="button"
        aria-label="Edit translation"
        data-testid="phrase-edit-pencil"
        data-tooltip-id={PHRASE_EDIT_PENCIL_TOOLTIP_ID}
        data-tooltip-content="Edit translation · or double-click the phrase"
        style={{ left: placement.left, top: placement.top }}
        // A press on the pencil must not move the caret or start a selection
        // in the column underneath, or the click lands on a changed selection.
        onMouseDown={e => e.preventDefault()}
        onClick={e => {
          e.stopPropagation();
          onEdit();
        }}
        onMouseEnter={() => onHoverChange(true)}
        onMouseLeave={() => onHoverChange(false)}
      >
        <SimplePencilIcon width="13" height="13" />
      </Pencil>
      <Tooltip
        id={PHRASE_EDIT_PENCIL_TOOLTIP_ID}
        place="top"
        style={COVERAGE_TOOLTIP_STYLE}
        delayShow={300}
      />
    </>,
    document.body,
  );
};

/**
 * Beside the end of the phrase's last line: its last client rect's right edge,
 * vertically centred on that line. Clamped to the viewport's right edge so a
 * phrase ending at the column's edge still gets its pencil (folded inward).
 */
export function computePencilPlacement(anchor: HTMLElement): {
  left: number;
  top: number;
} {
  const rects = anchor.getClientRects();
  const last =
    rects.length > 0 ? rects[rects.length - 1] : anchor.getBoundingClientRect();
  const viewportWidth =
    typeof window === 'undefined' ? Infinity : window.innerWidth;
  const left = Math.min(
    last.right + GAP,
    viewportWidth - PENCIL_SIZE - VIEWPORT_MARGIN,
  );
  const top = last.top + (last.height - PENCIL_SIZE) / 2;
  return { left, top };
}

const Pencil = styled.button`
  position: fixed;
  width: ${PENCIL_SIZE}px;
  height: ${PENCIL_SIZE}px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 4px;
  /* Readable at a glance (PLAT-11650 review feedback): a raised tile with the
     glyph in the surface's own gold — the caret and playhead already speak it
     here — so the way in is found without hunting for a grey hairline. */
  background: var(--surface-elevated);
  color: var(--accent);
  box-shadow: var(--elevation-toast);
  cursor: pointer;
  user-select: none;
  z-index: var(
    ${TOP_LAYER_VARS.contextMenu},
    ${CLIP_PREVIEW_Z.translationEditorPopover}
  );

  &:hover,
  &:focus-visible {
    color: var(--accent);
    background: var(--surface-elevated-hover);
  }
  &:focus-visible {
    outline: 2px solid var(--focus-border);
    outline-offset: 1px;
  }
`;
